import {IFirebaseContext, ScriptVersions} from "./IFirebaseContext";
import {Dispatch} from "react";
import {Database, get as firebaseGet, getDatabase, off, limitToLast, onValue, orderByKey, push as firebasePush,
    query, ref, remove as firebaseRemove, set as firebaseSet} from "@firebase/database";
import {remove as removeStore, update as updateStore} from "../../redux/slices/firebase";

export abstract class ConcreteContext implements IFirebaseContext {

    protected readonly tracking: {[path: string]: string[]} = {};
    protected readonly dispatch: Dispatch<any>
    protected readonly database: Database;

    protected constructor(dispatch: Dispatch<any>) {
        this.dispatch = dispatch;
        this.database = getDatabase();
    }
    protected handleFirebaseException(command: string, exception: Error) {
        console.error(command);
        console.error(exception.message);
    }
    public get(path: string): Promise<unknown> {
        return firebaseGet(ref(this.database, path))
            .catch(exception => this.handleFirebaseException(`Push ${path}`, exception))
            .then(snapshot => snapshot ? snapshot.val() : null);
    }
    public push(path: string, value: unknown): Promise<string | null> {
        return firebasePush(ref(this.database, path), value)
            .catch(exception => this.handleFirebaseException(`Push ${path}`, exception))
            .then(reference => reference ? reference.key : null);
    }
    public last(path: string, count: number): Promise<unknown> {
        return firebaseGet(query(ref(this.database, path), orderByKey(), limitToLast(count)))
            .catch(exception => this.handleFirebaseException(`Query ${query.toString()}`, exception))
            .then(reference => reference ? reference.val() : null)
    }

    public remove(path: string) {
        return firebaseRemove(ref(this.database, path))
            .catch(exception => this.handleFirebaseException(`Remove ${path}`, exception));
    };
    public set(path: string, value: unknown) {
        return firebaseSet(ref(this.database, path), value)
            .catch(exception => this.handleFirebaseException(`Set ${path}`, exception))
    };
    public track(path: string, store: string[] = [path]) {
        this.tracking[path] = store;
        onValue(ref(this.database, path), snapshot => {
            this.dispatch(updateStore({store, update: snapshot.val()}));
        });
    };
    public untrack(path: string) {
        off(ref(this.database, path));
        if (this.tracking[path]) {
            this.dispatch(removeStore({remove: this.tracking[path]}));
        }
        delete this.tracking[path];
    };
    public getScriptVersions(project: string, state: string): Promise<ScriptVersions> {
        return firebaseGet(ref(this.database, `versions/${project}/script/${state}`)).then(snapshot => {
            return snapshot.val() || {};
        });
    }
    public getUser(userKey: string) {
        return firebaseGet(ref(this.database, `users/${userKey}`)).then(snapshot => snapshot.val());
    }

    abstract pushComment(project: string, value: unknown): Promise<string | null>
    abstract pushCommentReply(project: string, comment: string, value: unknown): Promise<string | null>
    abstract pushIgnoreGrammar(project: string, state: string, value: unknown): Promise<string | null>
    abstract pushNotification(user: string, details: unknown): Promise<string | null>
    abstract pushState(project: string, value: unknown): Promise<string | null>
    abstract removeInvite(invite: string): Promise<void>
    abstract removeLock(project: string, state: string): Promise<void>
    abstract removeNetwork(project: string): Promise<void>
    abstract removeRegister(project: string, register: string): Promise<void>
    abstract removeState(project: string, state: string): Promise<void>
    abstract setCommentMember(project: string, commentId: string, member: unknown, value: unknown): Promise<void>
    abstract setCommentReplyMember(project: string, commentId: string, reply: string, member: unknown, value: unknown): Promise<void>
    abstract setDictionaryEntry(project: string, user: string, value: string): Promise<void>
    abstract setGrammar(project: string, state: string, value: unknown): Promise<void>
    abstract setLock(project: string, state: string, user: string): Promise<void>
    abstract setNetworkMember(project: string, member: string, value: unknown): Promise<void>
    abstract setProjectMember(project: string, member: string, value: unknown): Promise<void>
    abstract setRegister(project: string, register: string, value: unknown): Promise<void>
    abstract setStateMember(project: string, state: string, member: string, value: unknown): Promise<void>
    abstract setStatePosition(project: string, state: string, value: unknown): Promise<void>
    abstract setStateTransitionMember(project: string, state: string, transition: number, member: string, value: unknown): Promise<void>
    abstract setUserMember(user: string, member: string, value: unknown): Promise<void>
    abstract trackComments(project: string): void
    abstract trackDictionary(project: string): void
    abstract trackGrammar(network: string): void
    abstract trackSheets(project: string): void
    abstract trackIgnoredGrammar(network: string): void
    abstract trackInvites(): void
    abstract trackNetwork(network: string, filter: boolean): void
    abstract trackUser(user: string): void
    abstract trackUsers(): void
    abstract untrackNetwork(network: string): void
    abstract untrackUser(user: string): void
}